/**
 * 优化的文本渲染hook
 * 专门用于减少Vocabulary Quiz中的重复计算和渲染
 */

import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { WordHoverCard } from '@/components/vocabulary/WordHoverCard';
import { PracticeWordCard } from '@/components/vocabulary/PracticeWordCard';
import { UserSettings } from '@/contexts/user-settings-context';

interface WordExplanation {
  translation?: string;
  example?: string;
  usphone?: string;
}

interface UseOptimizedTextRendererProps {
  wordHighlightEnabled: boolean;
  practiceMode: boolean;
  vocabularyTerms: string[];
  practiceModeType?: 'vocabulary' | 'toefl';
  isWordCheckerReady: boolean;
  userSettings?: UserSettings;
  explanations: Record<string, WordExplanation>;
  loading: Record<string, boolean>;
  savedWords: Record<string, boolean>;
  savingWord: Record<string, boolean>;
  getExplanation: (word: string) => void;
  saveCard: (word: string, options?: { episodeId?: string; episodeTitle?: string; contextText?: string; episodeThumbnail?: string; youdaoTranslation?: any; status?: 'NEW' | 'FUZZY' | 'MASTERED' }) => void;
  isCardSaved: (word: string) => boolean;
  selectedWord: string;
  showWordDrawer: boolean;
  setSelectedWord: (word: string) => void;
  setShowWordDrawer: (show: boolean) => void;
  speakerRef?: React.RefObject<HTMLDivElement>;
  // episode上下文信息
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  hiddenWords: Record<string, boolean>;
  setHiddenWords: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  findPhraseInText?: (text: string, chunkIndex?: number) => Array<{
    phrase: string;
    original: string;
    position: { start: number; end: number };
    chunkIndex?: number;
  }>;
  // 添加有道翻译支持
  youdaoTranslations?: Record<string, any>;
  youdaoLoading?: Record<string, boolean>;
  youdaoGetTranslation?: (word: string) => void;
  youdaoIsInitialized?: boolean;
}

interface CachedRenderResult {
  text: string;
  practiceMode: boolean;
  hiddenWordsHash: string;
  result: React.ReactNode;
  timestamp: number;
}

export function useOptimizedTextRenderer(props: UseOptimizedTextRendererProps) {
  const {
    wordHighlightEnabled,
    practiceMode,
    vocabularyTerms,
    practiceModeType = 'toefl',
    isWordCheckerReady,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
    selectedWord,
    showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef,
    episodeId,
    episodeTitle,
    episodeThumbnail,
    hiddenWords,
    setHiddenWords,
    findPhraseInText,
    // 有道翻译参数
    // youdaoTranslations,
    // youdaoLoading,
    // youdaoGetTranslation,
    // youdaoIsInitialized
  } = props;

  // 缓存渲染结果 - 紧急内存优化
  const renderCache = useRef<Map<string, CachedRenderResult>>(new Map());
  const CACHE_TTL = 2 * 60 * 1000; // 减少到2分钟缓存
  const MAX_CACHE_SIZE = 30; // 减少最大缓存大小

  // 生成隐藏词汇的hash，用于缓存key
  const hiddenWordsHash = useMemo(() => {
    const keys = Object.keys(hiddenWords).sort();
    return keys.join('|');
  }, [hiddenWords]);

  // 缓存清理
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    const toDelete: string[] = [];
    
    renderCache.current.forEach((cached, key) => {
      if (now - cached.timestamp > CACHE_TTL) {
        toDelete.push(key);
      }
    });
    
    toDelete.forEach(key => renderCache.current.delete(key));
  }, []);

  // 生成缓存key
  const generateCacheKey = useCallback((text: string, practiceMode: boolean, hiddenWordsHash: string): string => {
    return `${text}-${practiceMode}-${hiddenWordsHash}`;
  }, []);

  // 渲染隐藏单词的高性能实现
  const renderTextWithHiddenWords = useCallback((
    text: string, 
    hiddenWordsList: Array<{
      phrase: string;
      original: string;
      position: { start: number; end: number };
    }>
  ): React.ReactNode => {
    const cacheKey = generateCacheKey(text, practiceMode, hiddenWordsHash);
    
    // 检查缓存
    const cached = renderCache.current.get(cacheKey);
    if (cached && 
        cached.text === text && 
        cached.practiceMode === practiceMode && 
        cached.hiddenWordsHash === hiddenWordsHash) {
      return cached.result;
    }

    // 如果没有隐藏的词汇，直接返回原文本
    if (hiddenWordsList.length === 0) {
      const result = <span>{text}</span>;
      
      // 缓存结果
      renderCache.current.set(cacheKey, {
        text,
        practiceMode,
        hiddenWordsHash,
        result,
        timestamp: Date.now()
      });
      
      return result;
    }

    // 按位置排序隐藏的词汇
    const sortedWords = [...hiddenWordsList].sort((a, b) => a.position.start - b.position.start);
    
    const elements: React.ReactNode[] = [];
    let lastEnd = 0;

    sortedWords.forEach((word, index) => {
      const { phrase, original, position } = word;
      const uniqueKey = `${phrase.toLowerCase()}-${position.start}-${position.end}`;
      const isRevealed = hiddenWords[uniqueKey];

      // 添加词汇前的文本
      if (position.start > lastEnd) {
        const beforeText = text.substring(lastEnd, position.start);
        if (beforeText) {
          elements.push(
            <span key={`before-${index}`}>
              {beforeText}
            </span>
          );
        }
      }

      // 添加词汇（隐藏或显示）
      // 注意：isRevealed = true 表示词汇应该显示，false 表示隐藏
      if (isRevealed) {
        // 显示已掌握的词汇
        elements.push(
          <span 
            key={`revealed-${index}`}
            className="font-medium text-[#d97757]"
          >
            {original}
          </span>
        );
      } else {
        // 显示隐藏的词汇（用下划线）
        elements.push(
          <span 
            key={`hidden-${index}`}
            className="cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setHiddenWords(prev => ({
                ...prev,
                [uniqueKey]: true // 点击后设置为 true（显示）
              }));
            }}
          >
            {original.split('').map((char, charIndex) => (
              <span key={charIndex}>
                {/\s/.test(char) ? (
                  char
                ) : (
                  <span 
                    className="border-b border-solid border-[#d97757] mx-[1px] inline-block" 
                    style={{ width: '0.5em', height: '0.5px' }}
                  />
                )}
              </span>
            ))}
          </span>
        );
      }

      lastEnd = position.end;
    });

    // 添加最后剩余的文本
    if (lastEnd < text.length) {
      const remainingText = text.substring(lastEnd);
      if (remainingText) {
        elements.push(
          <span key="remaining">
            {remainingText}
          </span>
        );
      }
    }

    const result = <span>{elements}</span>;

    // 缓存结果
    renderCache.current.set(cacheKey, {
      text,
      practiceMode,
      hiddenWordsHash,
      result,
      timestamp: Date.now()
    });

    // 定期清理缓存 - 紧急内存优化
    if (renderCache.current.size > MAX_CACHE_SIZE) {
      cleanupCache();
    }

    return result;
  }, [practiceMode, hiddenWords, hiddenWordsHash, generateCacheKey, cleanupCache]);

  // 渲染普通文本（带词汇高亮）的高性能实现
  const renderNormalText = useCallback((text: string): React.ReactNode => {
    const normalizedText = text.replace(/\u00a0/g, ' ');

    if (!wordHighlightEnabled || !isWordCheckerReady) {
      return <span>{normalizedText}</span>;
    }

    // 提取干净的单词用于缓存检查（去除标点符号）
    const extractCleanWords = (text: string): string[] => {
      // 使用更精确的正则表达式提取单词，包括缩写
      const wordMatches = text.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g) || [];
      return wordMatches.filter(word => word.length > 1); // 过滤掉单字符
    };

    const textWords = extractCleanWords(normalizedText);
    const hasVocabData = textWords.some(word => explanations[word] || loading[word]);
    
    const cacheKey = `normal-${normalizedText}-${wordHighlightEnabled}`;
    const cached = renderCache.current.get(cacheKey);
    
    // 只有在没有词汇数据时才使用缓存
    if (cached && !hasVocabData) {
      return cached.result;
    }

    // 改进的分词逻辑：使用正则表达式匹配来分割文本
    // 这个正则会捕获：单词（包括缩写）、数字、百分号、空格、标点符号
    const tokens = normalizedText.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g) || [];
    
    const elements = tokens.map((token, index) => {
      // 检查是否为有效的英文单词（包括缩写）
      const isValidWord = /^[a-zA-Z]+(?:'[a-zA-Z]+)?$/.test(token);
      
      if (!isValidWord) {
        // 非单词token（空格、标点等）直接返回
        return <span key={index}>{token}</span>;
      }

      // 对于有效单词，使用WordHoverCard渲染
      const variant = wordHighlightEnabled ? 'default' : 'subtitle';

      return (
        <WordHoverCard
          key={index}
          word={token}
          index={index}
          variant={variant}
          isSearchMatch={true} // Word Highlight模式使用原有的解释服务
          isWordCheckerReady={isWordCheckerReady}
          userSettings={userSettings}
          explanations={explanations}
          loading={loading}
          savedWords={savedWords}
          savingWord={savingWord}
          onGetExplanation={getExplanation}
          onSaveCard={saveCard}
          isCardSaved={isCardSaved}
          selectedWord={selectedWord}
          showWordDrawer={showWordDrawer}
          setSelectedWord={setSelectedWord}
          setShowWordDrawer={setShowWordDrawer}
          speakerRef={speakerRef}
          episodeId={episodeId}
          episodeTitle={episodeTitle}
          episodeThumbnail={episodeThumbnail}
          contextText={text}
          enableMobileDrawer={false}
        />
      );
    });

    const result = <span>{elements}</span>;

    // 只缓存没有词汇数据的文本结果
    if (!hasVocabData) {
      renderCache.current.set(cacheKey, {
        text,
        practiceMode: false,
        hiddenWordsHash: '',
        result,
        timestamp: Date.now()
      });
    }

    return result;
  }, [
    wordHighlightEnabled,
    isWordCheckerReady,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
    selectedWord,
    showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef
  ]);

  // Practice模式渲染：支持Key Words模式的短语检测
  const renderPracticeText = useCallback((text: string, chunkIndex: number = -1): React.ReactNode => {
    if (!text || typeof text !== 'string') {
      return text || "";
    }

    // TOEFL模式需要wordChecker，Key Words模式需要vocabularyTerms
    if (practiceModeType === 'toefl' && !isWordCheckerReady) {
      return <span>{text}</span>;
    }
    
    if (practiceModeType === 'vocabulary' && vocabularyTerms.length === 0) {
      return <span>{text}</span>;
    }

    // Key Words模式：尝试使用findPhraseInText查找短语
    if (practiceModeType === 'vocabulary' && findPhraseInText && chunkIndex !== -1) {
      
      const phrases = findPhraseInText(text, chunkIndex);
      
      
      if (phrases.length > 0) {
        const elements: React.ReactNode[] = [];
        let lastIndex = 0;
        
        phrases.forEach(({ phrase, position }) => {
          // 渲染短语前的文本
          if (position.start > lastIndex) {
            const textBefore = text.substring(lastIndex, position.start);
            elements.push(<span key={`text-${lastIndex}`}>{textBefore}</span>);
          }
          
          // 渲染短语（隐藏词汇）
          const originalPhrase = text.substring(position.start, position.end);
          const uniqueKey = `${phrase.toLowerCase()}-${position.start}-${position.end}`;
          const isRevealed = hiddenWords[uniqueKey] === true;
          
          
          if (isRevealed) {
            elements.push(
              <span 
                key={`phrase-${position.start}`}
                className="font-medium text-[#d97757]"
              >
                {originalPhrase}
              </span>
            );
          } else {
            elements.push(
              <span 
                key={`phrase-${position.start}`}
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHiddenWords(prev => ({
                    ...prev,
                    [uniqueKey]: true
                  }));
                }}
              >
                {originalPhrase.split('').map((char, i) => (
                  <span key={i}>
                    {/\s/.test(char) ? (
                      char
                    ) : (
                      <span 
                        className="border-b border-solid border-[#d97757] mx-[1px] inline-block" 
                        style={{ width: '0.5em', height: '0.5px' }}
                      />
                    )}
                  </span>
                ))}
              </span>
            );
          }
          
          lastIndex = position.end;
        });
        
        // 渲染剩余文本
        if (lastIndex < text.length) {
          const textAfter = text.substring(lastIndex);
          elements.push(<span key={`text-${lastIndex}`}>{textAfter}</span>);
        }
        
        return <>{elements}</>;
      }
    }
    
    // Key Words模式：如果没有找到短语，直接返回普通文本（不使用单词检测）
    if (practiceModeType === 'vocabulary') {
      return <span>{text}</span>;
    }
    
    // TOEFL模式：使用PracticeWordCard逐词渲染
    const tokens = text.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g) || [];
    
    const tokenElements = tokens.map((token, index) => {
      const isValidWord = /^[a-zA-Z]+(?:'[a-zA-Z]+)?$/.test(token);
      
      if (!isValidWord) {
        return <span key={index}>{token}</span>;
      }

      return (
        <PracticeWordCard
          key={index}
          word={token}
          index={index}
          chunkIndex={chunkIndex}
          hiddenWords={hiddenWords}
          setHiddenWords={setHiddenWords}
          isWordCheckerReady={isWordCheckerReady}
          vocabularyTerms={vocabularyTerms}
          practiceModeType={practiceModeType}
        />
      );
    });

    return <span>{tokenElements}</span>;
  }, [
    practiceMode,
    practiceModeType,
    isWordCheckerReady,
    vocabularyTerms,
    findPhraseInText,
    hiddenWords,
    setHiddenWords
  ]);

  // 主渲染函数
  const renderText = useCallback((
    text: string,
    chunkIndex?: number,
    // hiddenWordsList?: Array<{
    //   phrase: string;
    //   original: string;
    //   position: { start: number; end: number };
    // }>
  ): React.ReactNode => {
    if (practiceMode) {
      return renderPracticeText(text, chunkIndex || -1);
    } else {
      return renderNormalText(text);
    }
  }, [practiceMode, renderPracticeText, renderNormalText]);

  // 获取缓存统计
  const getCacheStats = useCallback(() => {
    const keys: string[] = [];
    renderCache.current.forEach((_, key) => {
      if (keys.length < 10) {
        keys.push(key);
      }
    });
    
    return {
      size: renderCache.current.size,
      keys
    };
  }, []);

  // 清理缓存
  const clearCache = useCallback(() => {
    renderCache.current.clear();
  }, []);

  // 组件卸载时清理缓存 - 紧急内存优化
  useEffect(() => {
    return () => {
      renderCache.current.clear();
    };
  }, []);

  return {
    renderText,
    getCacheStats,
    clearCache,
    renderTextWithHiddenWords,
    renderNormalText
  };
}
